import React, { Component } from "react";
import axios from "axios";
import { inject, observer } from "mobx-react";
import { Dropdown, Segment, Button } from "semantic-ui-react";
import { Instant, DateTimeFormatter } from "js-joda";
import moment from "moment";
import { Message } from "semantic-ui-react";

import { localDateTimeFromApi, momentFromApi } from "../utils/dateTime";
import { parseCurrencyPairString } from "../utils/botUtils";
import DateRangePicker from "../components/DateRangePicker";
import CandleStickChart from "./CandleStickChart";
import { getAuthHttpHeaders } from "../auth";

const ChartType = {
  STANDARD: { text: "Standard candles", value: "STANDARD" },
  HEIKIN_ASHI: { text: "Heikin-Ashi candles", value: "HEIKIN_ASHI" },
};

const MAX_DATA_STICKS_LOAD = 3000;
const MIN_DATA_STICKS_LOAD = 50;

const PERIODS = { m1: 60, m5: 300, m15: 900, m30: 1800, h1: 3600, h2: 7200, h4: 14400, d1: 86400 };

@inject("store")
@observer
class CandleSticksPanel extends Component {
  state = {
    selectedCurrencyPair: null,
    period: "m5",
    endDate: moment().startOf("day"),
    startDate: moment()
      .startOf("day")
      .subtract(1, "d"),
    chartType: ChartType.STANDARD.value,
    chartData: null,
    selectedTradeId: null,
    message: null,
    botInstanceId: null,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState && prevState.botInstanceId !== nextProps.botInstanceId) {
      return {
        message: null,
        chartData: null,
        selectedCurrencyPair: null,
        selectedTradeId: null,
        botInstanceId: nextProps.botInstanceId,
      };
    }

    return null;
  }

  load = () => {
    const { exchangeType } = this.props;
    const { selectedCurrencyPair, period, startDate, endDate } = this.state;

    if (!selectedCurrencyPair || !period || !startDate || !endDate || startDate.isSameOrAfter(endDate)) return;

    const sticksCount = endDate.diff(startDate, "seconds") / PERIODS[period];
    if (sticksCount > MAX_DATA_STICKS_LOAD) {
      setTimeout(() => {
        this.setState({
          message:
            "Too much for loading. Minimal period for selected date range is " +
            this.findBestPeriod(startDate, endDate, period),
        });
      });
      return;
    }

    if (this.state.message !== null) {
      this.setState({ message: null });
    }

    this.loadChartData(exchangeType, selectedCurrencyPair, period, startDate.unix(), endDate.unix());
  };

  loadChartData = async (exchangeType, selectedCurrencyPair, period, startDate, endDate) => {
    if (this.loading) return;

    const startInstant = Instant.ofEpochSecond(startDate);
    const endInstant = Instant.ofEpochSecond(endDate);
    const currencyPair = parseCurrencyPairString(selectedCurrencyPair);

    console.debug(
      `Loading chart for currency pair ${selectedCurrencyPair}, period ${period}, start ${startDate} (${startInstant}), end ${endDate} (${endInstant})`
    );

    this.loading = true;
    this.setState({ loading: true });

    // sleep for 0.5 seconds
    await (ms => {
      return new Promise(resolve => setTimeout(resolve, ms));
    })(500);

    try {
      let axiosRequest = axios.create({
        baseURL: "api",
        timeout: 30 * 1000,
        headers: {
          Accept: "application/json",
          ...getAuthHttpHeaders(),
        },
        params: {
          exchangeType: exchangeType,
          base: currencyPair.base,
          counter: currencyPair.counter,
          start: startDate,
          end: endDate,
          period: period,
        },
      });

      const response = await axiosRequest.get("chart/fetchData");
      let chartData = response.data.chartData;

      let message = null;
      if (!Array.isArray(chartData)) {
        console.error("Error fetching data: " + JSON.stringify(chartData));
        chartData = [];
        message = response.data.errorMessage || "Failed loading chart data. See console for error details.";
      }

      this.setState({ chartData, message, loading: false });
      this.loading = false;
    } catch (e) {
      alert(e);
    }
  };

  static getCurrencyPairs(trades, fromDate, toDate) {
    const currencySet = new Set();
    for (let trade of trades) {
      const openDate = momentFromApi(trade.openDate);
      const closeDate = momentFromApi(trade.closeDate);

      if (
        openDate.isAfter(fromDate) &&
        openDate.isBefore(toDate) &&
        closeDate.isAfter(fromDate) &&
        closeDate.isBefore(toDate)
      ) {
        currencySet.add(trade.base + "/" + trade.counter);
      }
    }

    return Array.from(currencySet);
  }

  prepareCurrencyPairDropDownOptions() {
    let currencies;
    if (this.props.currencyPairs) {
      currencies = this.props.currencyPairs;
    } else {
      currencies = CandleSticksPanel.getCurrencyPairs(this.props.trades, this.state.startDate, this.state.endDate);
    }

    return currencies.map(pair => {
      return {
        text: pair,
        value: pair,
      };
    });
  }

  static preparePeriodOptions() {
    return Object.keys(PERIODS).map(period => {
      return {
        text: period,
        value: period,
      };
    });
  }

  static prepareChartOptions() {
    return [ChartType.STANDARD, ChartType.HEIKIN_ASHI];
  }

  prepareTradesOptions() {
    const {
      store: { zoneId },
    } = this.props;

    const trades = this.props.trades.map(trade => {
      const dateTime = localDateTimeFromApi(trade.openDate, zoneId).format(
        DateTimeFormatter.ofPattern("d.MM.yy HH:mm")
      );

      return {
        text: `${trade.base} ${dateTime}`,
        value: trade.id,
      };
    });

    return trades.length > 1000 ? trades.slice(0, 1000) : trades;
  }

  findBestPeriod(startDate, endDate, period) {
    const findPeriodIndex = p => period === p;

    const periodKeys = Object.keys(PERIODS);

    // if the (startDate, endDate) interval is too wide for candles, change automatically the period
    let sticksCount = endDate.diff(startDate, "seconds") / PERIODS[period];

    while (sticksCount > MAX_DATA_STICKS_LOAD) {
      const newPeriod = periodKeys[periodKeys.findIndex(findPeriodIndex) + 1];
      if (newPeriod) {
        period = newPeriod;
        sticksCount = endDate.diff(startDate, "seconds") / PERIODS[period];
      } else break;
    }

    // if the (startDate, endDate) interval is too narrow for candles, change automatically the period
    while (sticksCount < MIN_DATA_STICKS_LOAD) {
      const newPeriod = periodKeys[periodKeys.findIndex(findPeriodIndex) - 1];

      if (newPeriod) {
        period = newPeriod;
        sticksCount = endDate.diff(startDate, "seconds") / PERIODS[period];
      } else break;
    }

    return period;
  }

  onCurrencyPairChanged = (e, { value }) => {
    const { period, startDate, endDate } = this.state;

    this.setState({
      period: this.findBestPeriod(startDate, endDate, period),
      selectedCurrencyPair: value,
      selectedTradeId: null,
      chartData: null,
    });
  };

  onPeriodChanged = (e, { value }) => this.setState({ period: value, chartData: null });
  onChartTypeChanged = (e, { value }) => this.setState({ chartType: value });

  onDateRangeChanged = ({ startDate, endDate }) => {
    const newPeriod = this.findBestPeriod(startDate, endDate, this.state.period);
    this.setState({ startDate, endDate, period: newPeriod, selectedTradeId: null, chartData: null });
  };

  onTradeChanged = (e, { value }) => {
    const trade = this.props.trades.find(t => t.id === value);
    this.changeTrade(trade);
  };

  changeTrade(trade) {
    const openDateMoment = momentFromApi(trade.openDate);

    let startDate, endDate;

    if (isTradeClosed(trade)) {
      startDate = openDateMoment.clone().subtract(12, "hours");
      endDate = momentFromApi(trade.closeDate)
        .clone()
        .add(12, "hours");
    } else {
      startDate = openDateMoment.clone().subtract(16, "hours");
      endDate = openDateMoment.clone().add(96, "hours");
      endDate = endDate.isAfter(moment()) ? moment() : endDate;
    }

    this.setState({
      period: this.findBestPeriod(startDate, endDate, "m5"),
      selectedTradeId: trade.id,
      chartData: null,
      selectedCurrencyPair: trade.base + "/" + trade.counter,
      startDate,
      endDate,
    });
  }

  getSelectedTradeIndex = () => {
    if (!this.state.selectedTradeId) return -1;
    return this.props.trades.findIndex(trade => this.state.selectedTradeId === trade.id);
  };

  selectPreviousTrade = () => {
    this.changeTrade(this.props.trades[this.getSelectedTradeIndex() - 1]);
  };

  selectNextTrade = () => {
    this.changeTrade(this.props.trades[this.getSelectedTradeIndex() + 1]);
  };

  static prepareCandlesData(chartTypeValue, chartData) {
    switch (chartTypeValue) {
      case ChartType.STANDARD.value:
        return CandleSticksPanel.prepareStandartCandlesData(chartData);
      case ChartType.HEIKIN_ASHI.value:
        return CandleSticksPanel.prepareHeikinAshiCandles(chartData);
      default:
        return [];
    }
  }

  static prepareStandartCandlesData(chartData) {
    return chartData.map(d => {
      return { x: d.timestamp.epochSecond || d.timestamp, open: d.open, close: d.close, high: d.high, low: d.low };
    });
  }

  static prepareHeikinAshiCandles(chartData) {
    const victoryData = [];

    for (let i = 0; i < chartData.length; ++i) {
      if (i === 0) {
        const d = chartData[i];
        victoryData.push({
          x: d.timestamp.epochSecond || d.timestamp,
          open: d.open,
          close: d.close,
          high: d.high,
          low: d.low,
        });
        continue;
      }

      // xOpen = [xOpen(Previous Bar) + xClose(Previous Bar)]/2
      const xOpen = (victoryData[i - 1].open + victoryData[i - 1].close) / 2;

      // xClose = (Open+High+Low+Close)/4
      const xClose = (chartData[i].open + chartData[i].close + chartData[i].high + chartData[i].low) / 4;

      // xHigh = Max(High, xOpen, xClose)
      const xHigh = Math.max(chartData[i].high, xOpen, xClose);

      // xLow = Min(Low, xOpen, xClose)
      const xLow = Math.min(chartData[i].low, xOpen, xClose);

      victoryData.push({
        x: chartData[i].timestamp.epochSecond || chartData[i].timestamp,
        open: xOpen,
        close: xClose,
        high: xHigh,
        low: xLow,
      });
    }

    return victoryData;
  }

  render() {
    const { config, trades, singleTradeMode } = this.props;
    const {
      message,
      chartData,
      chartType,
      startDate,
      endDate,
      selectedCurrencyPair,
      period,
      selectedTradeId,
    } = this.state;

    const messages = [];
    if (message) messages.push(message);

    if (startDate.isSameOrAfter(endDate)) {
      messages.push("Invalid date interval!");
    } else if (!chartData) {
      // load chart data if they don't exist
      setTimeout(() => this.load());
    }

    if (singleTradeMode && !selectedTradeId) setTimeout(() => this.selectNextTrade());

    const messageElements = messages.map((e, i) => (
      <Message key={i}>
        <p>{e}</p>
      </Message>
    ));

    let candlesData = CandleSticksPanel.prepareCandlesData(chartType, chartData || []);

    const selectedTradeIndex = selectedTradeId ? this.getSelectedTradeIndex() : undefined;
    const isSelectedFirst = selectedTradeIndex === 0;
    const isSelectedLast = selectedTradeIndex === trades.length - 1;

    return (
      <div>
        <Segment>
          <Dropdown
            value={chartType}
            selection
            style={{ marginRight: "6px" }}
            options={CandleSticksPanel.prepareChartOptions()}
            onChange={this.onChartTypeChanged}
          />
          <Dropdown
            placeholder="Select currency pair"
            value={selectedCurrencyPair}
            search
            selection
            style={{ marginRight: "6px", display: singleTradeMode ? "none" : null }}
            options={this.prepareCurrencyPairDropDownOptions()}
            onChange={this.onCurrencyPairChanged}
          />
          <Dropdown
            value={period}
            selection
            style={{ marginRight: "6px" }}
            options={CandleSticksPanel.preparePeriodOptions()}
            onChange={this.onPeriodChanged}
          />
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            style={{ marginRight: "6px", display: singleTradeMode ? "none" : null }}
            onChange={this.onDateRangeChanged}
          />
          <Dropdown
            value={selectedTradeId}
            selection
            style={{ marginRight: "4px", display: singleTradeMode ? "none" : null }}
            options={this.prepareTradesOptions()}
            onChange={this.onTradeChanged}
          />
          <Button
            icon="left arrow"
            style={{ display: singleTradeMode ? "none" : null }}
            disabled={!selectedTradeId || isSelectedFirst}
            onClick={this.selectPreviousTrade}
          />
          <Button
            icon="right arrow"
            style={{ display: singleTradeMode ? "none" : null }}
            disabled={!selectedTradeId || isSelectedLast}
            onClick={this.selectNextTrade}
          />
        </Segment>
        {messageElements}
        {selectedCurrencyPair && startDate.isBefore(endDate) ? (
          <CandleStickChart
            config={config}
            candlesData={candlesData}
            startDate={startDate}
            endDate={endDate}
            trades={trades}
            currencyPairString={selectedCurrencyPair}
          />
        ) : null}
      </div>
    );
  }
}

export function isTradeClosed(trade) {
  return trade.closeDate && trade.closeTotal && trade.closeAmount;
}

export default CandleSticksPanel;



// WEBPACK FOOTER //
// ./src/charts/CandleSticksPanel.js